import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { PaymentMethod } from '@stripe/stripe-js';
import { DateTime } from 'luxon';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import {
  ANIMATION_DURATION,
  APP_BANNER_HEIGHT,
  HEADER_Z_INDEX,
  IconButton,
  Layout,
  LoadingScreen,
  PageLayout,
  PillButton,
  Text
} from 'ds';
import PaymentMethodCard from 'ds/PaymentMethodCard';
import StripePaymentMethodModal from 'ds/StripePaymentMethodModal';
import { listPaymentMethods } from 'ds/StripePaymentMethodModal/requests';
import UpdateStripePaymentMethodModal from 'ds/UpdateStripePaymentMethodModal';
import { formatUsd } from 'helpers';
import { CLEANING_SERVICE_PATH, CLEANING_SERVICE_SET_UP_PATH } from 'routes';
import { actions } from 'store/CleaningService';
import {
  selectCleaningServicePricing,
  selectHasCleaningService,
  selectOnboardingServiceStartDate,
  selectOnboardingStandardDay
} from 'store/CleaningService/selectors';
import { actions as tenancyActions } from 'store/Tenancy';
import { selectServicesPaymentMethod } from 'store/Tenancy/selectors';
import { selectUIState } from 'store/UI/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';
import StickyHeader from 'ux/Layouts/Shared/StickyHeader';

import CleaningServiceCheckoutServiceBreakdown from './CleaningServiceCheckoutServiceBreakdown';
import CleaningServiceCostSummaryBox from './CleaningServiceCostSummaryBox';
import CleaningServiceLegalText from './CleaningServiceLegalText';
import CleaningServiceRevitalizeSpaceCard from './CleaningServiceRevitalizeSpaceCard';
import { calculateCleaningSchedule, getFirstOccurrenceOfWeekday } from './utils';
import { createCleaningService, getCleaningServicePricing, getCleaningServiceSchedules } from '../requests';

interface Props {}

const CleaningServiceCheckoutPage: React.FC<Props> = () => {
  const { isMobile } = useContext(media);
  const hasCleaningService = useAppSelector(selectHasCleaningService);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const tenancyId = useAppSelector(selectTenancyId);
  const servicesPaymentMethod = useAppSelector(selectServicesPaymentMethod);
  const cleaningServiceStartDate = useAppSelector(selectOnboardingServiceStartDate);
  const pricing = useAppSelector(selectCleaningServicePricing);
  const standardDay = useAppSelector(selectOnboardingStandardDay);
  const { appBannerType } = useAppSelector(selectUIState);
  const [showUpdatePaymentMethodModal, setShowUpdatePaymentMethodModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [paymentMethodLoaded, setPaymentMethodLoaded] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const loadPaymentMethods = useCallback(() => {
    if (!tenancyId) return;
    listPaymentMethods(tenancyId).then(({ data }) => {
      dispatch(tenancyActions.setPaymentMethods(data));

      const servicesPaymentMethod = data.find(paymentMethod => paymentMethod.metadata.services_payment_method);
      const rentPaymentMethod = data.find(paymentMethod => paymentMethod.metadata.rent_payment_method);
      dispatch(tenancyActions.setServicesPaymentMethod(servicesPaymentMethod));
      dispatch(tenancyActions.setRentPaymentMethod(rentPaymentMethod));
      setPaymentMethodLoaded(true);
    });
  }, [tenancyId, dispatch]);

  useEffect(() => {
    loadPaymentMethods();

    Promise.all([getCleaningServicePricing(), getCleaningServiceSchedules()]).then(([pricing, schedules]) => {
      dispatch(actions.setPricing(pricing.data));
      dispatch(actions.setSchedules(schedules.data));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (hasCleaningService) {
    return <Redirect to={`${CLEANING_SERVICE_PATH}`} />;
  }

  if (!cleaningServiceStartDate || !standardDay || !paymentMethodLoaded || !pricing) {
    return <LoadingScreen />;
  }

  const standardStartDate = getFirstOccurrenceOfWeekday(standardDay, cleaningServiceStartDate);

  const cleaningSchedule = calculateCleaningSchedule(DateTime.fromISO(cleaningServiceStartDate), standardDay, null);

  const firstMonthCleaningSchedule = cleaningSchedule[0];
  const firstMonthTotal = firstMonthCleaningSchedule.numStandardCleanings * pricing.cleaning_price;

  const handleStartService = async () => {
    setRequestLoading(true);
    if (!standardStartDate) {
      return;
    }

    try {
      const response = await createCleaningService({
        standardCleaningServiceStartDate: standardStartDate.toISO()
      });

      if (response.status === 201) {
        dispatch(actions.setSchedules(response.data));
        dispatch(actions.setOnboardingServiceStartDate(null));
        dispatch(actions.setOnboardingStandardDay(null));
        dispatch(actions.setOnboardingTouchUpDay(null));
        history.push(CLEANING_SERVICE_PATH);
      } else {
        showSnackbar({
          message: 'Error starting service. Please try again.',
          negative: true
        });
      }
    } catch (e) {
      showSnackbar({
        message: 'Error starting service. Please try again.',
        negative: true
      });
      console.error(e);
    }
    setRequestLoading(false);
  };

  if (isMobile) {
    return (
      <Layout>
        <StickyHeader
          position="fixed"
          top={appBannerType ? APP_BANNER_HEIGHT : 0}
          zIndex={HEADER_Z_INDEX}
          shadow={false}
          transition="background 200ms"
          height={60}
          align="center"
          width="100%"
        >
          <Layout
            {...(isMobile ? { paddingRight: 60, paddingLeft: 12 } : { paddingX: 40 })}
            direction="row"
            align="center"
            width="100%"
          >
            <IconButton
              name={'leftChevron'}
              size="md"
              iconSize={28}
              type="noBackground"
              onClick={() => {
                history.push(CLEANING_SERVICE_SET_UP_PATH);
              }}
            />
            <Layout width="100%">
              <Text size="headline-sm" align="center">
                Start your cleaning service
              </Text>
            </Layout>
          </Layout>
        </StickyHeader>
        <Layout flex width="100%" direction="column" marginTop={60}>
          <CleaningServiceRevitalizeSpaceCard />
          <Layout minHeight={8} color="gray-25" position="relative" width="100%" />
          <CleaningServiceCheckoutServiceBreakdown />
          <Layout minHeight={8} color="gray-25" position="relative" width="100%" />
          <Layout direction="column" gap={25} paddingX={24} paddingY={24}>
            <Text size="headline-sm">Billing method</Text>
            <PaymentMethodCard
              onClick={() => setShowUpdatePaymentMethodModal(true)}
              onUpdate={() => setShowUpdatePaymentMethodModal(true)}
            />
          </Layout>
          <Layout minHeight={8} color="gray-25" position="relative" width="100%" />
          <CleaningServiceCostSummaryBox />
        </Layout>
        <StripePaymentMethodModal
          isVisible={showPaymentMethodModal}
          onUpdate={loadPaymentMethods}
          onClose={() => {
            setShowPaymentMethodModal(false);
          }}
          onAddNew={paymentMethod => {
            setSelectedPaymentMethod(paymentMethod);
          }}
        />
        <UpdateStripePaymentMethodModal
          showManagePaymentMethods
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          isVisible={showUpdatePaymentMethodModal}
          type="services"
          onClose={() => {
            if (showPaymentMethodModal) return;
            setShowUpdatePaymentMethodModal(false);
            setTimeout(() => {
              setSelectedPaymentMethod(null);
            }, ANIMATION_DURATION);
          }}
          onSelectAddNewPaymentMethod={() => {
            setShowPaymentMethodModal(true);
          }}
        />
      </Layout>
    );
  }

  return (
    <PageLayout
      type="default"
      backLink={{ href: CLEANING_SERVICE_SET_UP_PATH }}
      title=" "
      maxWidth={1298}
      hasBorderBottom
      navBarTopOffset={appBannerType ? APP_BANNER_HEIGHT : 0}
    >
      <Layout direction="row" gap={80}>
        <Layout flex width={724} direction="column" gap={48}>
          <Text size="headline-lg">Start your cleaning service</Text>
          <CleaningServiceRevitalizeSpaceCard />
          <CleaningServiceCheckoutServiceBreakdown />
          <Layout direction="column" gap={24}>
            <Text size="headline-sm">Billing method</Text>
            <PaymentMethodCard
              onClick={() => setShowUpdatePaymentMethodModal(true)}
              onUpdate={() => setShowUpdatePaymentMethodModal(true)}
            />
          </Layout>
          <Layout direction="column" gap={24}>
            <Layout direction="row" gap={24} align="center">
              <Layout width={272}>
                <PillButton
                  fullWidth
                  type="purpleGradient"
                  text={'Start service'}
                  size="lg"
                  onClick={handleStartService}
                  disabled={requestLoading || !servicesPaymentMethod}
                  showSpinner={requestLoading}
                />
              </Layout>
              <Text size="headline-xs">
                Est. {firstMonthCleaningSchedule.month} total: {formatUsd(firstMonthTotal)}
              </Text>
            </Layout>
            <CleaningServiceLegalText />
          </Layout>
        </Layout>
        <CleaningServiceCostSummaryBox />
      </Layout>
      <StripePaymentMethodModal
        isVisible={showPaymentMethodModal}
        onUpdate={loadPaymentMethods}
        onClose={() => {
          setShowPaymentMethodModal(false);
        }}
        onAddNew={paymentMethod => {
          setSelectedPaymentMethod(paymentMethod);
        }}
      />
      <UpdateStripePaymentMethodModal
        showManagePaymentMethods
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        isVisible={showUpdatePaymentMethodModal}
        type="services"
        onClose={() => {
          if (showPaymentMethodModal) return;
          setShowUpdatePaymentMethodModal(false);
          setTimeout(() => {
            setSelectedPaymentMethod(null);
          }, ANIMATION_DURATION);
        }}
        onSelectAddNewPaymentMethod={() => {
          setShowPaymentMethodModal(true);
        }}
      />
    </PageLayout>
  );
};

export default CleaningServiceCheckoutPage;
